import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Button } from "@mui/material";
import { apiVariable } from "../../../../api/apiVariable";
import useAuth from "../../../../hooks/useAuth";
import StatusSnackbar from "../../../General/StatusSnackbar/StatusSnackbar";
import GenericFormPopup from "../../../Fleet/GenericFormPopup/GenericFormPopup";
import {
  accidentFieldConfig,
  accidentInitialValues,
} from "../../../../formConfigs/Fleet/accidentFieldConfig";
import {
  fetchAllDrivers,
  fetchAssets,
} from "../../IncidentPopup/IncidentPopup";
import { getId } from "../../../HR/UpdateEmployeePopup/UpdateEmployeePopup";
import { fetchOwnerCompanies } from "../../TruckPopup/TruckPopup";
import dayjs from "dayjs";
import { CHICAGO_TIMEZONE } from "../../../../utils/dayjsTimezone";

const fetchDispatchers = async (token: string | null | undefined) => {
  const response = await axios.get(`${apiVariable}/getAllDispatchers`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data.map((dispatcher: any) => ({
    label: `${dispatcher.firstName} ${dispatcher.lastName} | ${dispatcher.email}`,
    id: dispatcher.id.toString(),
  }));
};

const AccidentPopup = () => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { token } = useAuth();

  const { data: allDrivers } = useQuery({
    queryKey: ["drivers"],
    queryFn: () => fetchAllDrivers(token),
    enabled: open,
  });

  const { data: trucks } = useQuery({
    queryKey: ["assets", "Truck"],
    queryFn: () => fetchAssets("Truck", token),
    enabled: open,
  });

  const { data: trailers } = useQuery({
    queryKey: ["assets", "Trailer"],
    queryFn: () => fetchAssets("Trailer", token),
    enabled: open,
  });

  const { data: dispatchers } = useQuery({
    queryKey: ["dispatchers"],
    queryFn: () => fetchDispatchers(token),
    enabled: open,
  });

  const { data: ownerCompanies } = useQuery({
    queryKey: ["ownerCompanies"],
    queryFn: () => fetchOwnerCompanies(token),
    enabled: open,
  });

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
  };
  const handleSnackbarClose = () => setStatus("idle");

  const mutation = useMutation({
    mutationFn: (data: any) =>
      axios.post(`${apiVariable}/accident`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    onSuccess: () => {
      setStatus("success");
      queryClient.invalidateQueries({ queryKey: ["accidents"] });
      handleClose();
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response.data || "Failed to submit accident");
    },
  });

  const submitHandler = (data: any) => {
    const finalData = {
      ...data,
      date: data.date
        ? dayjs.tz(data.date, CHICAGO_TIMEZONE).utc().toISOString()
        : null,
      driverId: getId(data.driverId),
      truckId: getId(data.truckId),
      trailerId: getId(data.trailerId),
      dispatcherId: getId(data.dispatcherId),
      ownerCompanyId: getId(data.ownerCompanyId),
    };
    mutation.mutate(finalData);
  };

  return (
    <>
      <Button variant="contained" color="primary" onClick={handleOpen}>
        Add Accident
      </Button>
      <GenericFormPopup
        formFields={accidentFieldConfig}
        initialValues={accidentInitialValues}
        title="Add Accident"
        onSubmit={submitHandler}
        open={open}
        onClose={handleClose}
        loading={mutation.isPending}
        autocompleteData={{
          drivers: allDrivers,
          trucks: trucks,
          trailers: trailers,
          dispatchers: dispatchers,
          ownerCompanies: ownerCompanies,
        }}
      />
      <StatusSnackbar
        status={status}
        successMessage={"Accident added successfully"}
        errorMessage={errorMessage}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};

export default AccidentPopup;
